import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { requestCrashReport } from '../../utils/feedbackPrefill';
import { useGlobalStore } from '../../stores/globalStore';
import { isPsiStaffOrDev } from '../../utils/psiStaff';

/** Class component, so the store is read directly rather than through a hook. */
const testKitchenUser = () => isPsiStaffOrDev(useGlobalStore.getState().userEmail);

/**
 * level:
 *   - "app"    : full-page fallback (dark bg, 100vh) - used at the root
 *   - "page"   : inline fallback for route content area
 *   - "widget" : minimal inline fallback for sidebar / toolbar / small panels
 *
 * label: optional name shown in the error detail for easier triage
 */
type FallbackLevel = 'app' | 'page' | 'widget';

interface ErrorBoundaryProps {
  children: ReactNode;
  level?: FallbackLevel;
  label?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const label = this.props.label ?? 'unknown';
    console.error(`[ErrorBoundary:${label}] caught:`, error, errorInfo);
    this.setState({ errorInfo });

    // Park a prefilled Test Kitchen report. The tree below this boundary is gone — at app level
    // that includes the Report button itself — so the hand-off goes through sessionStorage and is
    // picked up once the page recovers. No-op when the beta flag is off or the login isn't PSI.
    if (import.meta.env.VITE_FEEDBACK_ENABLED === 'true' && testKitchenUser()) {
      requestCrashReport(error, label, errorInfo.componentStack ?? undefined);
    }
  }

  /**
   * Sends the tester home, where ReportProblemToolbarButton picks up the parked prefill and opens
   * dialog with tag, severity, and the error message already filled in. A crash the tester never
   * describes is worth far less than one with a sentence of theirs attached.
   */
  private handleReportProblem = () => {
    window.location.href = '/dashboard';
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
  };

  private handleGoHome = () => {
    // Navigate via window.location so it works even if React Router is broken
    window.location.href = '/dashboard';
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const level = this.props.level ?? 'app';

    if (level === 'app') {
      return this.renderAppFallback();
    }
    if (level === 'widget') {
      return this.renderWidgetFallback();
    }
    return this.renderPageFallback();
  }

  /* ------------------------------------------------------------------ */
  /*  Full-page fallback (root-level, keeps the existing dark style)    */
  /* ------------------------------------------------------------------ */
  private renderAppFallback() {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: 40,
          background: '#0d1b2a',
          color: '#e2e8f0',
        }}
      >
        <i
          className="bi bi-exclamation-octagon"
          style={{ fontSize: 48, color: 'var(--color-negative)', marginBottom: 16 }}
        />
        <h2 style={{ margin: '0 0 8px 0' }}>Something went wrong</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: 24, textAlign: 'center', maxWidth: 400 }}>
          An unexpected error occurred. Please try refreshing the page.
        </p>
        {this.renderDetails()}
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 24px',
            background: '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 'var(--font-size-base)',
          }}
        >
          Reload Page
        </button>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Page-level fallback (inside MainLayout content area)              */
  /* ------------------------------------------------------------------ */
  private renderPageFallback() {
    return (
      // globalStyles.css fits it to a phone held sideways (.page-error-fallback).
      <div
        className="page-error-fallback"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 300,
          padding: 32,
          color: 'var(--text-primary, #e2e8f0)',
        }}
      >
        <i
          className="bi bi-exclamation-triangle"
          style={{ fontSize: 40, color: 'var(--color-warning)', marginBottom: 12 }}
        />
        <h3 style={{ margin: '0 0 6px 0' }}>This page encountered an error</h3>
        <p
          style={{
            color: 'var(--text-secondary, var(--text-muted))',
            marginBottom: 20,
            textAlign: 'center',
            maxWidth: 420,
            fontSize: 'var(--font-size-base)',
          }}
        >
          Something went wrong rendering this page. You can try again or navigate elsewhere.
        </p>
        {this.renderDetails()}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button onClick={this.handleReset} style={btnPrimary}>
            Try Again
          </button>
          <button onClick={this.handleGoHome} style={btnSecondary}>
            Go to Dashboard
          </button>
          {import.meta.env.VITE_FEEDBACK_ENABLED === 'true' && testKitchenUser() && (
            <button onClick={this.handleReportProblem} style={btnSecondary}>
              Report This Problem
            </button>
          )}
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Widget-level fallback (sidebar, toolbar, small panels)            */
  /* ------------------------------------------------------------------ */
  private renderWidgetFallback() {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 12,
          color: 'var(--text-secondary, var(--text-muted))',
          fontSize: 'var(--font-size-base)',
          gap: 8,
        }}
      >
        <i className="bi bi-exclamation-circle" style={{ color: 'var(--color-warning)' }} />
        <span>Error loading section</span>
        <button
          onClick={this.handleReset}
          style={{
            background: 'none',
            border: '1px solid currentColor',
            borderRadius: 4,
            color: 'inherit',
            cursor: 'pointer',
            padding: '2px 8px',
            fontSize: 'var(--font-size-small)',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Collapsible error details (app + page levels)                     */
  /* ------------------------------------------------------------------ */
  private renderDetails() {
    const { error, errorInfo, showDetails } = this.state;
    if (!error) return null;

    return (
      // Centred like the heading and buttons above and below it (the toggle sat at the
      // left edge of a 600px block); the details themselves read left-aligned.
      <div style={{ marginBottom: 20, width: '100%', maxWidth: 600, textAlign: 'center' }}>
        <button
          onClick={() => this.setState({ showDetails: !showDetails })}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-secondary, #64748b)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-small)',
            textDecoration: 'underline',
            padding: 0,
            marginBottom: 6,
          }}
        >
          {showDetails ? 'Hide details' : 'Show details'}
        </button>
        {showDetails && (
          <pre
            style={{
              fontSize: 'var(--font-size-small)',
              color: 'var(--text-secondary, #64748b)',
              background: 'rgba(255,255,255,0.05)',
              padding: 12,
              borderRadius: 8,
              overflow: 'auto',
              maxHeight: 220,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              textAlign: 'left',
            }}
          >
            {error.message}
            {errorInfo?.componentStack
              ? '\n\nComponent stack:' + errorInfo.componentStack
              : ''}
          </pre>
        )}
      </div>
    );
  }
}

/* Shared button styles for page-level fallback */
const btnPrimary: React.CSSProperties = {
  padding: '8px 20px',
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 'var(--font-size-base)',
};

const btnSecondary: React.CSSProperties = {
  padding: '8px 20px',
  background: 'transparent',
  color: 'var(--text-primary, #e2e8f0)',
  border: '1px solid var(--text-secondary, #64748b)',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 'var(--font-size-base)',
};
